import React, { useMemo } from 'react';
import cx from 'classnames';
import isMobile from 'is-mobile';
import pcStyles from './ReportLayout.module.less';
import mobileStyles from './ReportLayout.mobile.module.less';
import { uiPxToRem } from '@/utils/mobile';
import { Space } from 'antd';
import AnalysisIcon from './icons/analysis.svg?react';
import WebsiteIcon from './icons/website.svg?react';
import LineIcon from './icons/line.svg?react';
import CommonModalFactory from '@/plugins/ModalFactory';
import './md.css';
import { IReportDetail } from '@/constants/geo';
import { analysisApi } from '@/axios/analysis';
import { AnalysisReportModalContent, type AnalysisReportModalData } from './AnalysisReportModalContent';

interface ReportLayoutProps {
  oemConfig?: any;
  nav?: React.ReactNode;
  top?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  name?: string;
  dateInfo?: { year: number; date: string };
  reportDetail?: IReportDetail;
  showNav?: boolean;
}

const ReportLayout: React.FC<ReportLayoutProps> = ({
  oemConfig,
  name,
  dateInfo,
  children,
  className,
  reportDetail,
  showNav = true,
}) => {
  const { reportId, officialWebsite, companyName } = reportDetail || {};
  const styles = isMobile() ? mobileStyles : pcStyles;
  const bgImg = oemConfig?.backgroundImage || '';
  const bgColor = oemConfig?.backgroundColor || '#fff';
  // nav部分抽离
  const navNode = (
    <>
      <div className={styles['pr_xiaobing']}>
        <img className={styles['pr_xiaobing_logo']} src={oemConfig.logoUrl} />
      </div>
      <img className={styles['pr_bisou']} src={oemConfig.productName} />
    </>
  );
  const showAnalysis = async () => {
    openAnalysisModal(
      {
        analysisId: reportId,
      },
      {
        width: isMobile() ? 'calc(100vw - 32px)' : 800,
      }
    );
  };
  const renderPcTopBtns = () => {
    if (!reportId && !officialWebsite) return null;
    return (
      <Space size={12}>
        {reportId && (
          <div className={styles['pr_top_left_btn']} onClick={showAnalysis}>
            查看分析报告
          </div>
        )}
        {officialWebsite && (
          <div
            className={styles['pr_top_left_btn']}
            onClick={() => {
              window.open(officialWebsite, '_blank');
            }}
          >
            查看官网
          </div>
        )}
      </Space>
    );
  };
  const renderMobileTopBtns = () => {
    if (!reportId && !officialWebsite) return null;
    return (
      <div className={styles['pr_top_left_btns']}>
        {reportId && (
          <>
            <div className={styles['pr_top_left_btn']} onClick={showAnalysis}>
              <AnalysisIcon />
              查看分析报告
            </div>
            <LineIcon />
          </>
        )}
        {officialWebsite && (
          <div
            className={styles['pr_top_left_btn']}
            onClick={() => {
              window.open(officialWebsite, '_blank');
            }}
          >
            <WebsiteIcon />
            查看官网
          </div>
        )}
      </div>
    );
  };
  /** 顶部信息 */
  const renderTop = () => {
    return (
      <div className={styles['pr_top']}>
        <div className={styles['pr_top_left']}>
          <div className={styles['pr_top_left_company']}>{companyName}</div>
          <div className={styles['pr_top_left_title']}>{name}</div>
          {isMobile() ? null : renderPcTopBtns()}
        </div>
        <div
          className={styles['pr_top_right']}
          style={{
            background: `url(${oemConfig.foregroundImage}) no-repeat center`,
            backgroundSize: '100% 100%',
          }}
        >
          <div className={styles['pr_top_right_date']}>
            <div className={styles['pr_top_right_date_gen']}>生成日期</div>
            {isMobile() ? null : <div>{dateInfo?.year}</div>}
          </div>
          <span className={styles['pr_top_right_divider']} />
          <div className={styles['pr_top_right_time']}>{dateInfo?.date}</div>
        </div>
        {isMobile() ? renderMobileTopBtns() : null}
      </div>
    );
  };
  return (
    <div
      className={cx(styles['report'], className)}
      style={{
        background: `url(${bgImg}) no-repeat top center ${bgColor}`,
        backgroundSize: isMobile() ? `${uiPxToRem(375)} ${uiPxToRem(300)}` : '100vw',
      }}
    >
      {oemConfig.companyName === '无标题' ? null : showNav && <div className={styles['report-nav']}>{navNode}</div>}
      <div className={styles['report-main']}>
        {renderTop()}
        {children}
      </div>
    </div>
  );
};

export default ReportLayout;

const openAnalysisModal = CommonModalFactory<AnalysisReportModalData>(
  {
    title: '分析报告',
    width: 800,
    footer: null,
    styles: {
      body: {
        padding: '12px 0',
      },
    },
  },
  ({ data }) => {
    return <AnalysisReportModalContent data={data} />;
  }, async ({ analysisId }) => {
    const result = await analysisApi.getWithoutAuth(analysisId!);
    return {
      content: result.content,
      analysisId,
      sourceFileUrl: result.sourceFileUrl,
      sourceFileName: result.sourceFileName,
      sourceFileType: result.sourceFileType,
      previewFileUrl: result.previewFileUrl,
      previewFileType: result.previewFileType,
    }
  }
);
